<script setup lang="ts">
import { computed, nextTick, onMounted, shallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppHeader from '@app/components/AppHeader.vue';
import ContentShell from '@app/components/ContentShell.vue';
import MainNavigation from '@app/components/MainNavigation.vue';
import MobileNavigation from '@app/components/MobileNavigation.vue';
import NavigationManager from '@app/components/NavigationManager.vue';
import AppLayout from '@app/layouts/AppLayout.vue';
import { useAuthStore } from '@app/stores/auth';
import HelpDrawer from '@features/help/components/HelpDrawer.vue';
import HelpTourOverlay from '@features/help/components/HelpTourOverlay.vue';
import { fetchHelpTour, fetchHelpTours } from '@features/help/api/helpApi';
import type { HelpTour, HelpTourCatalogItem, HelpTourContinuation } from '@features/help/types';
import type { ThemeContext } from '@shared/composables/useTheme';
import { useAppNotifications } from '@shared/notifications';

defineProps<{
  theme: ThemeContext;
}>();

const auth = useAuthStore();
const notifications = useAppNotifications();
const route = useRoute();
const router = useRouter();
const version = __APP_VERSION__ ?? '0.1.0';
const helpDrawerOpen = shallowRef(false);
const tourOpen = shallowRef(false);
const helpTours = shallowRef<HelpTourCatalogItem[]>([]);
const activeTour = shallowRef<HelpTour | null>(null);
const tourProgressVersion = shallowRef(0);
const currentHelpKey = computed(() => (typeof route.meta.helpKey === 'string' ? route.meta.helpKey : null));
const helpDisabledReason = computed(() => {
  const disabled = route.meta.helpDisabled as { reason?: string } | undefined;
  return disabled?.reason ?? null;
});

const tourMatchesCurrentRoute = (tour: HelpTourCatalogItem) =>
  tour.route === '/help'
    ? route.path === tour.route || route.path.startsWith(`${tour.route}/`)
    : route.path === tour.route;

const currentTours = computed(() => helpTours.value.filter(tourMatchesCurrentRoute));

const tourStorageKey = (tour: HelpTourCatalogItem | HelpTour) =>
  `krafta.help.tours.${auth.user?.id ?? 'anonymous'}.${tour.key}.${tour.version}`;

const isTourCompleted = (tour: HelpTourCatalogItem | HelpTour) =>
  typeof window !== 'undefined' &&
  tourProgressVersion.value >= 0 &&
  Boolean(window.localStorage.getItem(tourStorageKey(tour)));

const completedTourKeys = computed(() => currentTours.value.filter(isTourCompleted).map((tour) => tour.key));

const activeTourContinuation = computed<HelpTourContinuation | null>(() => {
  const tour = activeTour.value;
  if (!tour) return null;

  const sameRouteTours = helpTours.value.filter((item) => item.route === tour.route);
  if (sameRouteTours.length < 2) return tour.continueTo ?? null;

  const currentIndex = sameRouteTours.findIndex((item) => item.key === tour.key);
  const orderedTours =
    currentIndex >= 0
      ? [...sameRouteTours.slice(currentIndex + 1), ...sameRouteTours.slice(0, currentIndex)]
      : sameRouteTours;
  const nextTour = orderedTours.find((item) => item.key !== tour.key && !isTourCompleted(item))
    ?? orderedTours.find((item) => item.key !== tour.key);

  if (!nextTour) return tour.continueTo ?? null;

  return {
    tourKey: nextTour.key,
    route: nextTour.route,
    label: nextTour.title
  };
});

const userInitials = computed(() => {
  const name = auth.user?.fullName ?? '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'OA';
});

const loadTours = async () => {
  if (!auth.isAuthenticated) {
    helpTours.value = [];
    return;
  }

  try {
    helpTours.value = await fetchHelpTours(auth.authHeaders);
  } catch (error) {
    console.warn('Failed to load help tours', error);
    helpTours.value = [];
  }
};

const startTourByKey = async (tourKey: string) => {
  try {
    activeTour.value = await fetchHelpTour(tourKey, auth.authHeaders);
    tourOpen.value = true;
  } catch (error) {
    console.warn('Failed to start help tour', error);
    notifications.error('Попробуйте ещё раз.', {
      title: 'Не удалось открыть подсказки'
    });
  }
};

const startCurrentTour = async (tourKey?: string) => {
  const tour = tourKey
    ? currentTours.value.find((item) => item.key === tourKey) ?? helpTours.value.find((item) => item.key === tourKey)
    : currentTours.value[0];
  if (!tour) return;
  await startTourByKey(tour.key);
};

const markTourDone = (tour: HelpTour) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(
      tourStorageKey(tour),
      JSON.stringify({
        completedAt: new Date().toISOString()
      })
    );
  }
  tourProgressVersion.value += 1;
};

const continueTourOnNextPage = async (tour: HelpTour, continuation: HelpTourContinuation) => {
  markTourDone(tour);
  tourOpen.value = false;
  activeTour.value = null;

  try {
    if (route.path !== continuation.route) {
      await router.push(continuation.route);
    }
    await nextTick();
    window.setTimeout(() => {
      void startTourByKey(continuation.tourKey);
    }, 250);
  } catch (error) {
    console.warn('Failed to continue help tour', error);
    notifications.error('Откройте помощь и запустите подсказки ещё раз.', {
      title: 'Не удалось продолжить подсказки'
    });
  }
};

onMounted(loadTours);

watch(
  () => [auth.isAuthenticated, auth.user?.id, auth.user?.roles?.join(',')] as const,
  loadTours
);

watch(
  () => route.path,
  () => {
    if (tourOpen.value) {
      tourOpen.value = false;
      activeTour.value = null;
    }
  }
);
</script>

<template>
  <NavigationManager
    v-slot="nav"
    :theme="theme"
  >
    <AppLayout :theme-classes="theme.themeClasses.value">
      <template #header>
        <AppHeader
          :version="version"
          :is-light="theme.isLight.value"
          :theme-label="theme.themeLabel.value"
          :theme-classes="{ header: theme.themeClasses.value.header }"
          :show-scanner-shortcut="nav.showScannerShortcut"
          :show-attendance-end-shift-shortcut="nav.showAttendanceEndShiftShortcut"
          :navigation-locked="nav.navigationLocked"
          :user-initials="userInitials"
          :user-name="auth.user?.fullName"
          :is-mobile-menu-open="nav.isMobileMenuOpen"
          :toggle-theme="theme.toggleTheme"
          :toggle-mobile-menu="nav.toggleMobileMenu"
          :logout="auth.logout"
          @open-help="helpDrawerOpen = true"
        />
      </template>

      <template #content>
        <ContentShell
          :layout-classes="nav.layoutClasses"
          :layout-variant="nav.layoutVariant"
          :theme-classes="{ main: theme.themeClasses.value.main, surface: theme.themeClasses.value.surface }"
          :show-navigation="nav.showNavigation"
          :show-mobile-navigation="nav.showMobileNavigation"
          :is-public-route="nav.isPublicRoute"
          class="flex-1"
        >
          <template #navigation>
            <MainNavigation
              :nav-links="nav.navLinks"
              :theme-classes="theme.themeClasses.value"
              :is-nav-link-active="nav.isNavLinkActive"
            />
          </template>

          <template #mobile-navigation>
            <MobileNavigation
              :nav-links="nav.navLinks"
              :theme-classes="theme.themeClasses.value"
              :is-nav-link-active="nav.isNavLinkActive"
              @navigate="nav.closeMobileMenu"
            />
          </template>

          <slot />
        </ContentShell>

        <HelpDrawer
          v-model="helpDrawerOpen"
          :help-key="currentHelpKey"
          :fallback-reason="helpDisabledReason"
          :tours="currentTours"
          :completed-tour-keys="completedTourKeys"
          @start-tour="startCurrentTour"
        />

        <HelpTourOverlay
          v-model="tourOpen"
          :tour="activeTour"
          :continuation="activeTourContinuation"
          @finish="markTourDone"
          @skip="markTourDone"
          @continue="continueTourOnNextPage"
        />
      </template>
    </AppLayout>
  </NavigationManager>
</template>
